/**
 * MakeoverStorySection — the stat-rich value strip that sits right
 * below the hero. Combines two citation-backed research stats with
 * three "why it matters" pillars and a primary CTA that scrolls to
 * the pricing card.
 *
 * Visual: light, Apple-quiet. Two large stat cards on the left, three
 * compact pillar cards on the right (collapses to stacked columns on
 * mobile). No gradient noise, no emoji.
 */

import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Search,
  Shield,
  MessageSquare,
  ArrowRight,
  BarChart3,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  fadeUp,
  kicker,
  displaySection,
  sectionWrap,
  sectionInner,
} from './shared';

const STATS = [
  {
    headline: '>8%',
    label: 'more likely to be hired',
    body:
      'A randomized field experiment published as an NBER working paper found that job seekers given AI-based résumé writing assistance were hired 8% more often. MIT Sloan also reported 7.8% more job offers for the assisted group.',
    icon: TrendingUp,
    tone: 'from-sky-500/15 to-sky-500/0',
    accent: 'text-sky-600',
    testid: 'makeover-stat-hire-rate',
  },
  {
    headline: '>71%',
    label: 'higher interview callback rate',
    body:
      'In ResumeGo’s field experiment, applications linking to a comprehensive LinkedIn profile received a 13.5% callback rate — materially higher than the 7.9% baseline.',
    icon: BarChart3,
    tone: 'from-rose-500/15 to-rose-500/0',
    accent: 'text-rose-600',
    testid: 'makeover-stat-callback',
  },
];

const PILLARS = [
  {
    icon: Search,
    title: 'Discoverability',
    body:
      'Highlight relevant skills and role-specific keywords to help recruiters find and recognise your fit.',
    testid: 'makeover-pillar-discoverability',
  },
  {
    icon: Shield,
    title: 'Trust',
    body:
      'Present a complete, consistent story of your experience and achievements across both.',
    testid: 'makeover-pillar-trust',
  },
  {
    icon: MessageSquare,
    title: 'Interview opportunities',
    body:
      'Make it easier for recruiters to understand your value and see a reason to start a conversation.',
    testid: 'makeover-pillar-interviews',
  },
];

const scrollToPricing = () => {
  const el =
    document.querySelector('[data-testid="home-hero-checkout-card"]') ||
    document.getElementById('pricing');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const MakeoverStorySection = () => (
  <section
    className={sectionWrap}
    aria-label="Why a stronger résumé and LinkedIn matter"
    data-testid="makeover-story-section"
  >
    <div className={sectionInner}>
      <motion.div {...fadeUp} className="max-w-3xl">
        <p className={kicker}>Why this matters</p>
        <h2
          className={`${displaySection} mt-4 text-4xl sm:text-5xl lg:text-6xl`}
          data-testid="makeover-story-headline"
        >
          Get discovered. Build trust.{' '}
          <span className="text-primary">Earn more interview opportunities.</span>
        </h2>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
          Your experience matters. How clearly you present it matters too.
          A well-written résumé and a comprehensive LinkedIn profile help
          recruiters find your skills, understand your strengths, and assess
          your fit.
        </p>
      </motion.div>

      {/* Two stat cards */}
      <div className="mt-12 grid gap-5 md:grid-cols-2">
        {STATS.map((s, i) => (
          <motion.div
            key={s.headline}
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.05 + i * 0.07 }}
            className="relative overflow-hidden rounded-3xl border border-border/70 bg-card/80 backdrop-blur p-7 sm:p-9"
            data-testid={s.testid}
          >
            <div
              aria-hidden="true"
              className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${s.tone}`}
            />
            <div className="relative">
              <s.icon
                size={22}
                className={`${s.accent} mb-5`}
                strokeWidth={2}
                aria-hidden="true"
              />
              <p
                className={`font-display font-semibold tracking-[-0.04em] ${s.accent} text-6xl sm:text-7xl leading-none`}
              >
                {s.headline}
              </p>
              <p className="mt-2 text-base font-semibold text-foreground/90">
                {s.label}
              </p>
              <p className="mt-4 text-[13.5px] leading-relaxed text-muted-foreground max-w-prose">
                {s.body}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Three pillars */}
      <div className="mt-16">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className={kicker}>The three outcomes</p>
          <h3 className="mt-3 font-display font-semibold tracking-[-0.02em] text-2xl sm:text-3xl text-foreground">
            Make your résumé and LinkedIn work together.
          </h3>
        </motion.div>

        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.05 + i * 0.05 }}
              className="rounded-3xl border border-border/70 bg-card/70 p-7"
              data-testid={p.testid}
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <p.icon size={18} strokeWidth={2.2} aria-hidden="true" />
              </span>
              <h4 className="mt-5 text-lg font-semibold tracking-[-0.01em]">
                {p.title}
              </h4>
              <p className="mt-2 text-[13.5px] text-muted-foreground leading-relaxed">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
          <Button
            size="lg"
            onClick={scrollToPricing}
            className="h-12 rounded-full px-7 text-[14.5px]"
            data-testid="makeover-story-cta"
          >
            Strengthen my résumé + LinkedIn
            <ArrowRight size={16} className="ml-2" aria-hidden="true" />
          </Button>
          <p className="text-[11.5px] text-muted-foreground max-w-sm">
            *Research findings are from separate studies. Individual outcomes
            vary.
          </p>
        </div>
      </div>
    </div>
  </section>
);

export default MakeoverStorySection;
